import { useEffect, useMemo } from 'react';
import { useSocket } from './SocketProvider';
import { LiveFeed, LiveFeedEvent } from '@repo/types';
import { Match, SocketEventType, SocketMessage } from '@repo/types';
import { useLiveFeedQuery } from '@data/liveFeed/useLiveFeedQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useTodaysMatchesQuery } from '@data/matches/useTodaysMatchesQuery';

export type EnahncedLiveFeedEvent = Omit<LiveFeedEvent, 'match'> & {
  match: Match | null;
};
export type EnhancedLiveFeed = {
  date: string;
  events: EnahncedLiveFeedEvent[];
};

export const useLiveFeed = (): EnhancedLiveFeed | null => {
  const socket = useSocket();
  const queryClient = useQueryClient();

  const matches = useTodaysMatchesQuery();

  const liveFeedDate = useMemo(() => {
    // return '2025-08-23';
    const today = new Date();
    return today.toISOString().substring(0, 10);
  }, []);

  const liveFeed = useLiveFeedQuery(liveFeedDate);

  useEffect(() => {
    socket.on('message', (msg: SocketMessage) => {
      if (msg.type === SocketEventType.NewLiveFeedEvents) {
        const newLiveFeed = msg.payload;
        queryClient.setQueryData(['live-feed', { date: newLiveFeed.date }], (old: LiveFeed) => {
          if (!old) return newLiveFeed;
          return {
            ...old,
            events: [...newLiveFeed.events, ...old.events],
          };
        });
      }
    });

    // cleanup listener when leaving this page
    return () => {
      console.log('removing socket listener');
      socket.off('message');
    };
  }, [socket, queryClient]);

  const enhancedLiveFeed = useMemo(() => {
    if (liveFeed?.data?.events && matches?.data?.items) {
      const enhancedEvents = liveFeed.data.events.map((event) => {
        const match = matches.data.items.find((match) => match.id === event.match);

        return {
          ...event,
          match: match ?? null,
        };
      });

      return {
        date: liveFeed.data._id,
        events: enhancedEvents,
      };
    }

    return null;
  }, [liveFeed?.data?._id, liveFeed?.data?.events, matches?.data?.items]);

  return enhancedLiveFeed;
};
